import { Box as Step, Grid, Stack as StepActions } from '@mui/material'
import Breadcrumbs from 'components/Breadcrumbs'
import Label from 'components/Label'
import Layout from 'layouts/Default'
import { useMemo, useRef } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import StepButtonGroup from './components/FormStepper/components/StepButtonGroup'
import StepContent from './components/FormStepper/components/StepContent'
import Stepper from './components/FormStepper/components/Stepper'
import { useStepper } from 'hooks/stepper.hook'
import { useHearingForms } from './hooks/hearing-form.hook'

export type OutletContextType = {
  formRefs: { current: { submit: () => Promise<void> }[] }
  currentStep: number
}

type SubmitRef = Record<number, { submit?: () => Promise<void> }>

export const CreateHearingPage = () => {
  const { id } = useParams()
  const { isFinalized, formSteps } = useHearingForms(id)

  const formRefs = useRef<SubmitRef>({})

  const {
    step: { current, isFirst, isLast },
    handleChange,
    handleNext,
    handlePrev,
  } = useStepper(formSteps)

  const stepTitle = useMemo(() => formSteps[current].title, [current])
  const prevButtonLabel = useMemo(
    () => (!isFirst ? formSteps[current - 1].title : 'Voltar'),
    [current],
  )
  const nextButtonLabel = useMemo(() => (!isLast ? formSteps[current + 1].title : ''), [current])

  const handleStepAndSave = async (callback: () => void) => {
    const submitObj = formRefs.current[current]
    if (submitObj?.submit) {
      try {
        await submitObj.submit()
        callback()
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handlePrevStepAndSave = () => handleStepAndSave(handlePrev)
  const handleNextStepAndSave = () => handleStepAndSave(handleNext)
  const handleChangeStepAndSave = (index: number) => handleStepAndSave(() => handleChange(index))

  const handleSaveDraft = async () => {
    const submitObj = formRefs.current[current]
    try {
      await submitObj?.submit?.()
    } catch (error) {
      console.error(error)
    }
  }

  const handleFinish = async () => {
    const submitObj = formRefs.current[current]
    try {
      await submitObj?.submit?.()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Layout>
      <Grid container spacing={1}>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Breadcrumbs>
            <Label text='Nova Oitiva' bold v={'body1'} />
          </Breadcrumbs>
        </Grid>

        <Grid item xs={12}>
          <Stepper steps={formSteps} activeStep={current} onChangeStep={handleChangeStepAndSave} />
          <Step sx={{ mt: 4, width: '100%' }}>
            <StepContent title={stepTitle} currentStep={current}>
              <Outlet context={{ formRefs, currentStep: current }} />
            </StepContent>
            <StepActions
              display='flex'
              direction='row'
              justifyContent='center'
              spacing={2}
              sx={{ pt: 2, pb: 2 }}
            >
              <StepButtonGroup
                isFirstStep={isFirst}
                isLastStep={isLast}
                onPrev={handlePrevStepAndSave}
                onNext={handleNextStepAndSave}
                onSaveDraft={handleSaveDraft}
                onFinish={handleFinish}
                prevButtonLabel={prevButtonLabel}
                nextButtonLabel={nextButtonLabel}
                isFinalized={isFinalized}
              />
            </StepActions>
          </Step>
        </Grid>
      </Grid>
    </Layout>
  )
}
